import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withModifiers as _withModifiers, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

_pushScopeId("data-v-0aa89fce")
const _hoisted_1 = { class: "explanation" }
_popScopeId()

import { computed, inject, reactive } from "vue";
import type { PropType } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { callPatchEntitlement } from "@/api/licensing";
import type { Toasts, UsageCounter, UsageCounterUpdateDetails } from "@/types";


export default /*#__PURE__*/_defineComponent({
  props: {
  isOpen: {
    type: Boolean,
    required: true
  },
  entitlementId: {
    type: String,
    required: true
  },
  counter: {
    type: Object as PropType<UsageCounter>,
    required: true
  }
},
  emits: ["cancel", "updated"],
  setup(__props, { emit }) {

const props = __props




const { t } = useI18n();
const store = useStore();
const toasts = inject<Toasts | undefined>("toasts");
const handleError = inject<((error: unknown) => void) | undefined>("handleError");

const state = reactive({
  isOpen: computed(() => props.isOpen),
  allowance: props.counter.allowance ?? 0,
  remainingAllowance: props.counter.remainingAllowance ?? 0,
  loading: false
});

const counterLabel = computed(
  () => props.counter.name ?? t("entitlements.counters.default")
);

const validatePositiveNumber = (value: unknown) =>
  (Number.isFinite(Number(value)) && Number(value) >= 0) ||
  t("validation.positiveNumber");

const validateRemainingAllowance = (value: unknown) =>
  Number(value) <= Number(state.allowance) ||
  t("assignments.editQuota.validation.remainingExceedsAllowance");

const isFormValid = computed(() => {
  const allowance = Number(state.allowance);
  const remaining = Number(state.remainingAllowance);
  return (
    Number.isFinite(allowance) &&
    Number.isFinite(remaining) &&
    allowance >= 0 &&
    remaining >= 0 &&
    remaining <= allowance
  );
});

const buildCounterUpdate = (): UsageCounterUpdateDetails => ({
  // A null name designates the default counter of the entitlement.
  name: props.counter.isDefault ? null : props.counter.name ?? null,
  allowance: Number(state.allowance),
  remainingAllowance: Number(state.remainingAllowance)
});

const handleSubmit = async () => {
  if (!isFormValid.value || state.loading) {
    return;
  }
  state.loading = true;
  try {
    const response = await callPatchEntitlement(props.entitlementId, {
      usages: [buildCounterUpdate()]
    });
    store.commit("licensing/INIT_ENTITLEMENT_AND_EDIT", response.data);
    toasts?.info(t("entitlements.counters.editModal.success"));
    emit("updated");
  } catch (error: any) {
    handleError?.(error?.response?.data ?? error);
    toasts?.error(t("entitlements.counters.editModal.error"));
  } finally {
    state.loading = false;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_va_input = _resolveComponent("va-input")!
  const _component_va_form = _resolveComponent("va-form")!
  const _component_va_button = _resolveComponent("va-button")!
  const _component_va_modal = _resolveComponent("va-modal")!

  return (_openBlock(), _createBlock(_component_va_modal, {
    modelValue: _unref(state).isOpen,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_unref(state).isOpen = $event)),
    "hide-default-actions": "",
    title: _ctx.$t('entitlements.counters.editModal.title', { counter: _unref(counterLabel) })
  }, {
    footer: _withCtx(() => [
      _createVNode(_component_va_button, {
        flat: "",
        outline: "",
        class: "mr-1",
        disabled: _unref(state).loading,
        onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('cancel')))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t("buttons.cancel")), 1)
        ]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(_component_va_button, {
        class: "ml-1",
        color: "success",
        loading: _unref(state).loading,
        disabled: !_unref(isFormValid),
        onClick: handleSubmit
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t("buttons.save")), 1)
        ]),
        _: 1
      }, 8, ["loading", "disabled"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("p", _hoisted_1, _toDisplayString(_ctx.$t("entitlements.counters.editModal.explanation")), 1),
      _createVNode(_component_va_form, {
        ref: (_value, _refs) => {
          _refs['form'] = _value
        },
        onSubmit: _withModifiers(handleSubmit, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createVNode(_component_va_input, {
            modelValue: _unref(state).allowance,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_unref(state).allowance = $event)),
            modelModifiers: { number: true },
            type: "number",
            min: "0",
            class: "control-margin",
            label: _ctx.$t('entitlements.counters.editModal.allowance'),
            rules: [validatePositiveNumber],
            disabled: _unref(state).loading
          }, null, 8, ["modelValue", "label", "rules", "disabled"]),
          _createVNode(_component_va_input, {
            modelValue: _unref(state).remainingAllowance,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_unref(state).remainingAllowance = $event)),
            modelModifiers: { number: true },
            type: "number",
            min: "0",
            class: "control-margin",
            label: _ctx.$t('entitlements.counters.editModal.remainingAllowance'),
            rules: [validatePositiveNumber, validateRemainingAllowance],
            disabled: _unref(state).loading
          }, null, 8, ["modelValue", "label", "rules", "disabled"])
        ]),
        _: 1
      }, 8, ["onSubmit"])
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})